import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import Authenticator from "./Authenticator";
import ConnectedDevice, { ConnectedDeviceOptions } from "./ConnectedDevice";
import WebSocketClient, { WebSocketClientOptions } from "./WebSocketClient";

import {
  HTTP_ACCESS_HEADER,
  HTTP_CLIENT_APP_HEADER,
  HTTP_CLIENT_ID_HEADER,
  HTTP_LOCALE_HEADER,
  HTTP_REFRESH_HEADER,
  HTTP_TOKEN_EXPIRE_CODE,
  REFRESH_TOKEN_ROUTE,
} from "./settings";

import Api from "./Api";

export type ClientUserInfo = {
  deviceId?: string;
  userId?: string;
  locale?: string;
};

export interface ClientOptions {
  ws: WebSocketClientOptions;
  getUserInfo: () => Promise<ClientUserInfo>;
  baseUrl: string;
  variant: string;
  onRefreshSuccess: (data: any) => any;
  onLogout: () => any;
  connectedDevice: ConnectedDeviceOptions;
  secureStorage?: {
    getItem: (key: string) => Promise<string>;
    setItem: (key: string, value: string) => Promise<void>;
  };
}

let reqInt: any;
let resInt: any;

export type SharedClient = AxiosInstance & {
  authenticator: Authenticator;
  device: ConnectedDevice;
  ws: WebSocketClient;
  api: Api;
  logout: () => void;
};

const isRefreshRequest = (config: AxiosRequestConfig) =>
  config.url?.startsWith(REFRESH_TOKEN_ROUTE.path);

const onRequest =
  (options: ClientOptions, client: SharedClient) =>
  async (config: AxiosRequestConfig) => {
    try {
      const info = await options.getUserInfo();

      if (!config.headers) config.headers = {};

      try {
        if (!isRefreshRequest(config)) {
          const shouldHaveAccessToken = info.deviceId || info.userId;

          if (
            !client.authenticator.hasAccessToken &&
            shouldHaveAccessToken &&
            !config.url?.includes("/auth/logout")
          ) {
            await client.authenticator.refreshToken();
          }

          if (client.authenticator.refresh) {
            await client.authenticator.refresh;
          }
        }
      } catch (error) {
        console.error("failed to refresh token", error);
      }

      if (client.authenticator.hasAccessToken) {
        const authHeaderValue = "Bearer " + client.authenticator.accessToken;
        config.headers[HTTP_ACCESS_HEADER] = authHeaderValue;
      } else {
        delete config.headers[HTTP_ACCESS_HEADER];
      }

      try {
        const rt = await client.authenticator.asyncGetRefreshToken();
        if (rt) {
          config.headers[HTTP_REFRESH_HEADER] = rt;
        }
      } catch (error) {
        console.error("failed to retrive refresh token from secure store");
        console.error(error);
      }

      config.headers[HTTP_CLIENT_APP_HEADER] = options.variant;

      if (!client.device.id && client.device.promise) {
        await client.device.promise;
      }

      if (client.device.id)
        config.headers[HTTP_CLIENT_ID_HEADER] = client.device.id;

      // send client language
      if (info.locale) {
        config.headers[HTTP_LOCALE_HEADER] = info.locale;
      }
    } catch (error) {
      console.error("apply client request header err", error);
    } finally {
      return config;
    }
  };

const onResponse =
  (options: ClientOptions, client: SharedClient) =>
  async (error: AxiosError) => {
    const { config: originalReq, response } = error;
    if (
      response &&
      !isRefreshRequest(originalReq) && // not a refresh error;
      !originalReq["isRetryAttempt"] && // not a retry;
      response.status === HTTP_TOKEN_EXPIRE_CODE // access token expired;
    ) {
      try {
        originalReq["isRetryAttempt"] = true;
        await client.authenticator.refreshToken();
        return await client.request(originalReq);
      } catch (e) {
        throw e;
      }
    }
    throw error;
  };

function createClient(options: ClientOptions): SharedClient {
  const sc = axios.create({
    baseURL: options.baseUrl,
    withCredentials: true,
  }) as SharedClient;

  // order of declarations is important:

  // 1. connected device is required for authenticator;
  sc["device"] = new ConnectedDevice(options.connectedDevice);

  // 2. authenticator is required for ws;
  sc["authenticator"] = new Authenticator(sc, options);

  // 3. register websocket client
  sc["ws"] = new WebSocketClient(sc, options.ws);

  // 2. authenticator is required for ws;
  sc["api"] = new Api(sc);

  sc["logout"] = () => {
    sc.authenticator.clear();
    options?.onLogout?.();
  };

  if (reqInt !== undefined) {
    sc.interceptors.request.eject(reqInt);
  }

  if (resInt !== undefined) {
    sc.interceptors.response.eject(resInt);
  }

  reqInt = sc.interceptors.request.use(onRequest(options, sc));
  resInt = sc.interceptors.response.use((r) => r, onResponse(options, sc));

  return sc;
}

export default createClient;
